import { computed, inject, Injectable } from '@angular/core';
import { UserService } from './user.service';
import { PremiumModalService } from './premium-modal.service';
import { DEFAULT_TIER, isSubscriptionTier, PREMIUM_LIMITS, PREMIUM_MESSAGE, SUBSCRIPTION_TIERS, SubscriptionTier } from '../../shared';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private readonly userService = inject(UserService);
  private readonly premiumModal = inject(PremiumModalService);

  private readonly currentTier = computed<SubscriptionTier>(() => {
    const tier = this.userService.currentUser()?.subscriptionId;

    if (isSubscriptionTier(tier)) {
      return tier;
    }

    return DEFAULT_TIER;
  });

  private hasTier = (tier: SubscriptionTier) =>
  computed(() => this.currentTier() === tier);

  readonly isFree = this.hasTier(SUBSCRIPTION_TIERS.FREE);
  readonly isPremium = this.hasTier(SUBSCRIPTION_TIERS.PREMIUM);
  readonly isPro = this.hasTier(SUBSCRIPTION_TIERS.PRO); 

  /**
   * Whether the user can add another client.
   * Free: max FREE_TIER_MAX_CLIENTS (3). Premium: unlimited.
   */
  canAddClient(currentClientsCount: number): boolean {
    if (this.currentTier() === SUBSCRIPTION_TIERS.PREMIUM) 
      return true;

    if(this.currentTier() === SUBSCRIPTION_TIERS.PRO)
      return currentClientsCount < PREMIUM_LIMITS.PRO_TIER_MAX_CLIENTS;

    return currentClientsCount < PREMIUM_LIMITS.FREE_TIER_MAX_CLIENTS;
  }

  /**
   * Whether the user can add another company.
   * Free: max FREE_TIER_MAX_COMPANIES (1). Premium: unlimited.
   */
  canAddCompany(currentCompaniesCount: number): boolean {
    if (this.currentTier() === SUBSCRIPTION_TIERS.PREMIUM) 
      return true;

    if(this.currentTier() === SUBSCRIPTION_TIERS.PRO)
      return currentCompaniesCount < PREMIUM_LIMITS.PRO_TIER_MAX_COMPANIES;

    return currentCompaniesCount < PREMIUM_LIMITS.FREE_TIER_MAX_COMPANIES;
  }

  /**
   * Whether the user can use the PDF download feature.
   * Only available for premium subscription.
   */
  canUsePdf(): boolean {
    return this.currentTier() === SUBSCRIPTION_TIERS.PREMIUM || this.currentTier() === SUBSCRIPTION_TIERS.PRO;
  }

  checkAndShowPremium(type: 'client' | 'company', count: number): boolean {
    if (type === 'client') {
      return this.checkAndShowPremiumForNewClient(count);
    } else {
      return this.checkAndShowPremiumForNewCompany(count);
    }
  }

  /**
   * If the user cannot add a client (free tier at limit), opens the premium modal
   * and returns false. Otherwise returns true.
   */
  checkAndShowPremiumForNewClient(currentClientsCount: number): boolean {
    if (this.canAddClient(currentClientsCount))
      return true;
    
    if(this.currentTier() === SUBSCRIPTION_TIERS.FREE)
        this.premiumModal.open(PREMIUM_MESSAGE.clientsLimitFreeTier);
      
    if(this.currentTier() === SUBSCRIPTION_TIERS.PRO)
      this.premiumModal.open(PREMIUM_MESSAGE.clientsLimitProTier);

    return false;
  }

  /**
   * If the user cannot add a company (free tier at limit), opens the premium modal
   * and returns false. Otherwise returns true.
   */
  checkAndShowPremiumForNewCompany(currentCompaniesCount: number): boolean {
    if (this.canAddCompany(currentCompaniesCount)) 
      return true;
    
    if(this.currentTier() === SUBSCRIPTION_TIERS.FREE)
        this.premiumModal.open(PREMIUM_MESSAGE.companiesLimitFreeTier);
      
      if(this.currentTier() === SUBSCRIPTION_TIERS.PRO)
        this.premiumModal.open(PREMIUM_MESSAGE.companiesLimitProTier);

    return false;
  }

  /**
   * If the user cannot use PDF (free tier), opens the premium modal. Call when user
   * attempts to download invoice PDF.
   */
  checkAndShowPremiumForPdf(): boolean {
    if (this.canUsePdf()) return true;
    this.premiumModal.open(PREMIUM_MESSAGE.pdfFeature);
    return false;
  }
}
